import { call, delay, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import { accountsBalancesActions } from '../accounts-balances';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { QcsSyncResourcesConfigProps } from '../core/resourceStoreTypes';
import * as actions from './investmentHoldingsV2Actions';
import { getLastSyncDate } from './investmentHoldingsV2Selectors';
import { type InvestmentHoldingV2Type, mkInvestmentHoldingV2 } from './investmentHoldingsV2Types';

const mkQcsSyncHoldingsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<InvestmentHoldingV2Type>();

const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<InvestmentHoldingV2Type>>) =>
  mkQcsSyncHoldingsConfig({
    resourceName: 'investment holdings',
    resourceBaseUrl: '/v2/investments/holdings',
    getLastSyncDate,
    transformServerResourceToClientResource: (resource) =>
      mkInvestmentHoldingV2({
        ...resource,
        userCostBasis: resource.userCostBasis ?? null,
        userCostBasisOn: resource.userCostBasisOn ?? '',
        quantityWhenUserSetCostBasis: resource.quantityWhenUserSetCostBasis ?? null,
        symbol: resource.symbol ?? '',
        securityName: resource.securityName ?? '',
        localSecurityId: resource.localSecurityId ?? '',
      }),
    ...props,
  });

export function* investmentHoldingsDumpUpdater(resources: InvestmentHoldingV2Type[]) {
  const bumpHoldings: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpHoldings');
  if (bumpHoldings) {
    const holdings = resources.map(mkInvestmentHoldingV2);
    yield put(actions.getInvestmentHoldingsV2Success({ resources: holdings }, { context: 'BUMP_CHANGE_DUMP' }));
  }
}

export function* investmentHoldingsNudgeUpdater() {
  const bumpHoldings: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpHoldings');
  if (bumpHoldings) {
    yield put(actions.getInvestmentHoldingsV2(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

export function* investmentHoldingsCalcSucceedActionWatcher() {
  yield takeEvery(actions.investmentHoldingsCalcSuccess.type, function* investmentHoldingsCalcSuccess() {
    yield put(accountsBalancesActions.getAccountsBalances());
  });
}

export function* investmentHoldingsCalcActionWatcher() {
  const resourceConfig = mkQcsSyncHoldingsConfig({
    resourceName: 'investment holdings calc',
    resourceBaseUrl: '/v2/investments/holdings/manual-accounts-holdings',
    successAction: actions.investmentHoldingsCalcSuccess,
    failureAction: actions.investmentHoldingsCalcFailure,
  });
  while (true) {
    yield race([
      take(actions.createInvestmentHoldingV2Success.type),
      take(actions.updateInvestmentHoldingV2Success.type),
      take(actions.deleteInvestmentHoldingV2Success.type),
    ]);
    yield call(
      resourceSagaUtils.qcsSyncCreateResource<InvestmentHoldingV2Type, undefined>,
      resourceConfig,
      actions.investmentHoldingsCalc(),
    );
  }
}

// QCS's `SyncPriority` names which side of the sync wins, not how urgent the work is:
// TRANSACTIONS rewrites the holding from the transaction ledger (deleting it when no transactions
// are left), while HOLDINGS goes the other way and writes shim transactions so the ledger
// reconciles to the holding. SIMPL-33607 is the transactions-win direction.
const HOLDING_SYNC_PRIORITY = 'TRANSACTIONS';

// QCS answers the sync request with 202 and rebuilds the holding afterwards, so an immediate
// re-read would return the pre-sync values. Wait before refreshing; `takeLatest` collapses a
// burst of edits (or the two calls a security change produces) into a single refresh.
const HOLDING_SYNC_REFRESH_DELAY_MS = 2000;

export function* syncHoldingFromTransactionsActionWatcher() {
  yield takeEvery(actions.syncHoldingFromTransactions.type, resourceSagaUtils.qcsRequest, {
    method: 'POST',
    path: '/v2/investments/holdings/sync',
    params: (action: ReturnType<typeof actions.syncHoldingFromTransactions>) => ({
      accountId: action.payload.accountId,
      localSecurityId: action.payload.localSecurityId,
      priority: HOLDING_SYNC_PRIORITY,
    }),
    successAction: actions.syncHoldingFromTransactionsSuccess,
    failureAction: actions.syncHoldingFromTransactionsFailure,
    // Bookkeeping the user never requested directly: a failure leaves the holding as it was, so
    // don't interrupt the transaction edit they did request with an error toast.
    showError: false,
  });
}

export function* syncHoldingFromTransactionsSuccessActionWatcher() {
  yield takeLatest(actions.syncHoldingFromTransactionsSuccess.type, function* refreshSyncedHoldings() {
    yield delay(HOLDING_SYNC_REFRESH_DELAY_MS);
    yield put(actions.getInvestmentHoldingsV2(undefined, { context: 'HOLDING_SYNC_FROM_TRANSACTIONS' }));
    yield put(accountsBalancesActions.getAccountsBalances());
  });
}

export function* getInvestmentHoldingsV2ActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.getInvestmentHoldingsV2Success,
    failureAction: actions.getInvestmentHoldingsV2Failure,
  });
  yield resourceSagaUtils.takeAndDebounce(
    actions.getInvestmentHoldingsV2,
    resourceSagaUtils.qcsSyncGetResources<InvestmentHoldingV2Type>,
    resourceConfig,
  );
}

export function* createInvestmentHoldingV2ActionWatcher() {
  const resourceConfig = mkResourceConfig({
    applyResourceFromCRUDResponse: true,
    successAction: actions.createInvestmentHoldingV2Success,
    failureAction: actions.createInvestmentHoldingV2Failure,
  });
  yield takeEvery(
    actions.createInvestmentHoldingV2.type,
    resourceSagaUtils.qcsSyncCreateResource<InvestmentHoldingV2Type>,
    resourceConfig,
  );
}

export function* updateInvestmentHoldingV2ActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.updateInvestmentHoldingV2Success,
    failureAction: actions.updateInvestmentHoldingV2Failure,
  });
  yield takeEvery(
    actions.updateInvestmentHoldingV2.type,
    resourceSagaUtils.qcsSyncUpdateResource<InvestmentHoldingV2Type>,
    resourceConfig,
  );
}

export function* deleteInvestmentHoldingV2ActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.deleteInvestmentHoldingV2Success,
    failureAction: actions.deleteInvestmentHoldingV2Failure,
  });
  yield takeEvery(
    actions.deleteInvestmentHoldingV2.type,
    resourceSagaUtils.qcsSyncDeleteResource<InvestmentHoldingV2Type>,
    resourceConfig,
  );
}

export function* batchInvestmentHoldingsV2ActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.batchInvestmentHoldingsV2Success,
    failureAction: actions.batchInvestmentHoldingsV2Failure,
  });
  yield takeEvery(
    actions.batchInvestmentHoldingsV2.type,
    resourceSagaUtils.qcsSyncUpdateResources<InvestmentHoldingV2Type>,
    resourceConfig,
  );
}

export default [
  investmentHoldingsCalcSucceedActionWatcher,
  investmentHoldingsCalcActionWatcher,
  syncHoldingFromTransactionsActionWatcher,
  syncHoldingFromTransactionsSuccessActionWatcher,
  batchInvestmentHoldingsV2ActionWatcher,
  getInvestmentHoldingsV2ActionWatcher,
  createInvestmentHoldingV2ActionWatcher,
  updateInvestmentHoldingV2ActionWatcher,
  deleteInvestmentHoldingV2ActionWatcher,
];
